import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Form from '../../Framework/Components/Form/Form';
import TextInput from '../../Framework/Components/Form/TextInput';
import Number from '../../Framework/Components/Form/Number';
import BlockStyledEditor from '../../Framework/Components/Form/BlockStyledEditor';
import StaticAutoComplete from '../../Framework/Components/Form/StaticAutoComplete';
import Grid, { GridLeft, GridRight } from '../../Framework/Layouts/Grid';
import Toggle from '../../Framework/Components/Form/Toggle';
import AliasTextArea from '../../Framework/Components/Form/AliasTextArea';
import IngredientToAliasConverter from '../../Utils/Ingredient/IngredientToAliasConverter';
import routes from '../../Routes/routes';
import EntityUrlText from '../Products/EntityUrlText';
import { MessageContext } from '../../Framework/Context/MessageContext';
import style from '../../../scss/components/buttons/button.module.scss';

const IngredientForm = (props) => {
    const { service, entity } = props;
    const { pushMessage } = useContext(MessageContext);
    const urlParams = useParams();
    const ingredientId = urlParams.id;

    const [categories, setCategories] = useState();
    const [syncing, setSyncing] = useState(false);
    const [syncedAt, setSyncedAt] = useState(entity?.inci_synced_at ?? null);

    useEffect(() => {
        service.get('ingredients/get-categories').then((result) => {
            setCategories(
                result.map((item) => ({
                    id: item.id ?? item,
                    name: item.name,
                })),
            );
        });
    }, []);

    useEffect(() => {
        setSyncedAt(entity?.inci_synced_at ?? null);
    }, [entity?.inci_synced_at]);

    const syncSafetyData = async () => {
        if (!ingredientId || syncing) return;
        setSyncing(true);
        try {
            const response = await service.post('ingredients', `${ingredientId}/sync-safety-data`);
            pushMessage({ title: response?.message ?? 'Sync queued.', type: 'success' });
        } catch {
            pushMessage({ title: 'Failed to queue safety data sync.', type: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const formatSyncedAt = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleString('hu-HU');
    };

    return (
        <>
            <Form {...props}>
                <Grid>
                    <GridLeft>
                        <EntityUrlText
                            path={routes.ingredient}
                            buttonText="Ingredient"
                        />
                        <TextInput name="name" />
                        <BlockStyledEditor name="description" />
                        <IngredientToAliasConverter
                            service={service}
                            resource="ingredients"
                        />
                    </GridLeft>
                    <GridRight>
                        <Toggle name="is_top" />
                        <StaticAutoComplete
                            name="categories"
                            items={categories}
                            isMultiple
                        />
                        <Number name="ewg_score" min="0" />
                        <Number name="ewg_score_max" min="0" />
                        <Number name="comedogen_index" min="0" max="5" />
                        <Number name="irritative_index" min="0" max="5" />
                        <AliasTextArea />

                        {ingredientId && (
                            <div className="mt-4 border border-gray-200 rounded p-4">
                                <p className="text-sm font-semibold mb-2">INCI API Safety Sync</p>
                                {syncedAt && (
                                    <p className="text-xs text-gray-500 mb-2">
                                        Last synced: {formatSyncedAt(syncedAt)}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    className={`${style.button} ${style.basic}`}
                                    onClick={syncSafetyData}
                                    disabled={syncing}
                                >
                                    {syncing ? 'Syncing…' : 'Sync from INCI API'}
                                </button>
                            </div>
                        )}
                    </GridRight>
                </Grid>
            </Form>
        </>
    );
};

export default IngredientForm;

IngredientForm.propTypes = {
    service: PropTypes.object.isRequired,
    entity: PropTypes.object,
};

IngredientForm.defaultProps = {
    entity: null,
};
